import { localTodayCalendarDate } from '@shared/dates/calendarDate';

import type {
  Claim,
  ClaimUpdateRequest,
  InsuranceType,
  Sex,
  TaxIdType
} from '../../types';

import type { CMS1500FormData } from './ClaimCMS1500Form';

/**
 * Transform API claim data to CMS-1500 form format
 */
export const transformClaimToForm = (claim: Claim): CMS1500FormData => {

  // Get patient name from new split fields (API now uses _middle_initial)
  const patientName = {
    lastName: claim.patient_last_name || '',
    firstName: claim.patient_first_name || '',
    middleInitial: claim.patient_middle_initial || ''
  };

  // Get primary insured name from new split fields (API now uses _middle_initial)
  const insuredName = {
    lastName: claim.primary_insured_last_name || '',
    firstName: claim.primary_insured_first_name || '',
    middleInitial: claim.primary_insured_middle_initial || ''
  };

  // Parse patient address from API field
  const patientAddress = claim.patient_address ? {
    line1: claim.patient_address.line1 || '',
    line2: claim.patient_address.line2 || '',
    city: claim.patient_address.city || '',
    state: claim.patient_address.state || '',
    zip: claim.patient_address.zip || '',
    phone: `${claim.patient_address.area_code || ''}${claim.patient_address.phone || ''}`, // Combine area code and phone
  } : {
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
  };

  // Parse primary insured address from API field
  const insuredAddress = claim.primary_insured_address ? {
    line1: claim.primary_insured_address.line1 || '',
    line2: claim.primary_insured_address.line2 || '',
    city: claim.primary_insured_address.city || '',
    state: claim.primary_insured_address.state || '',
    zip: claim.primary_insured_address.zip || '',
    phone: `${claim.primary_insured_address.area_code || ''}${claim.primary_insured_address.phone || ''}`, // Combine area code and phone
  } : {
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
  };

  // Transform diagnoses to lettered format (A-L)
  const diagnosisCodes: CMS1500FormData['diagnosisCodes'] = {
    A: '',
    B: '',
    C: '',
    D: '',
    E: '',
    F: '',
    G: '',
    H: '',
    I: '',
    J: '',
    K: '',
    L: '',
  };

  if (claim.diagnoses) {
    const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
    claim.diagnoses.slice(0, 12).forEach((code, index) => {
      diagnosisCodes[letters[index] as keyof typeof diagnosisCodes] = code;
    });
  }

  // Transform service lines from claim_lines
  const serviceLines = (claim.claim_lines || []).slice(0, 6).map(line => ({
    dateFrom: line.date_from || '',
    dateTo: line.date_to || '',
    placeOfService: line.pos_code || '11',
    emg: '',
    cptHcpcs: line.cpt_code || '',
    modifiers: (line.modifiers || []).join(','),
    // Split modifiers into individual fields
    modifier1: line.modifiers?.[0] || '',
    modifier2: line.modifiers?.[1] || '',
    modifier3: line.modifiers?.[2] || '',
    modifier4: line.modifiers?.[3] || '',
    diagnosisPointer: (line.diagnosis_pointers || []).join(','),
    charges: ((line.charge_cents || 0) / 100).toFixed(2),
    units: String(line.units || '1'),
    epsdt: '',
    idQualifier: (line.qualifiers as { id_qualifier?: string } | null)?.id_qualifier || '',
    renderingProviderId: line.rendering_provider_npi || '',
  }));

  // Ensure we have at least one service line
  if (serviceLines.length === 0) {
    serviceLines.push({
      dateFrom: '',
      dateTo: '',
      placeOfService: '11',
      emg: '',
      cptHcpcs: '',
      modifiers: '',
      modifier1: '',
      modifier2: '',
      modifier3: '',
      modifier4: '',
      diagnosisPointer: '',
      charges: '',
      units: '1',
      epsdt: '',
      idQualifier: '',
      renderingProviderId: '',
    });
  }

  // Parse service facility - it's a flexible object in the API
  const serviceFacility = claim.service_facility as { 
    name?: string;
    line1?: string;
    line2?: string;
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
  } | null;
  const billingAddr = claim.billing_address;

  // Get insurance type from API field
  const getInsuranceType = (): CMS1500FormData['insuranceType'] => {
    // Use API field if available
    if (claim.insurance_type) {
      // Map API enum values to form values
      const typeMap: Record<string, CMS1500FormData['insuranceType']> = {
        'Medicare': 'medicare',
        'Medicaid': 'medicaid',
        'TRICARE': 'tricare',
        'CHAMPVA': 'champva',
        'GroupHealthPlan': 'groupHealth',
        'FECA': 'feca',
        'Other': 'other',
      };
      return typeMap[claim.insurance_type] || '';
    }
    return '';
  };

  return {
    // Box 1: Insurance Type (Payer)
    insuranceType: getInsuranceType(),

    // Box 1a
    insuredIdNumber: claim.primary_insured_id_number || '',

    // Box 2
    patientName,

    // Box 3
    patientBirthDate: claim.patient_dob || '',
    patientSex: claim.patient_sex === 'Male' ? 'M' : claim.patient_sex === 'Female' ? 'F' : '',

    // Box 4
    insuredName,

    // Box 5
    patientAddress,

    // Box 6
    patientRelationship: (claim.primary_insured_relationship as 'self' | 'spouse' | 'child' | 'other') || '',

    // Box 7
    insuredAddress,

    // Box 8
    reservedNUCC: '',

    // Box 9 - Other Insured Information (API now uses _middle_initial)
    otherInsuredName: {
      lastName: claim.other_insured_last_name || '',
      firstName: claim.other_insured_first_name || '',
      middleInitial: claim.other_insured_middle_initial || ''
    },
    otherInsuredPolicyGroup: claim.other_insured_policy_number || '',
    otherInsuredReservedNUCC: '', // Box 9b - Reserved NUCC field  
    otherInsuredReservedNUCC2: '', // Box 9c - Reserved NUCC field
    otherInsuredPlanName: claim.other_insured_plan_name || '',

    // Box 10
    conditionRelated: {
      employment: claim.patient_condition_employment_related === true ? true : claim.patient_condition_employment_related === false ? false : undefined,
      autoAccident: claim.patient_condition_auto_accident === true ? true : claim.patient_condition_auto_accident === false ? false : undefined,
      autoAccidentState: claim.patient_condition_auto_accident_state || '',
      otherAccident: claim.patient_condition_other_accident === true ? true : claim.patient_condition_other_accident === false ? false : undefined,
      claimCodes: claim.patient_condition_claim_codes || '',
    },

    // Box 11
    insuredPolicyGroup: claim.primary_insured_group_number || '',
    insuredDateOfBirth: claim.primary_insured_dob || '',
    insuredSex: claim.primary_insured_sex === 'Male' ? 'M' : claim.primary_insured_sex === 'Female' ? 'F' : '',
    insuredPlanName: claim.primary_insured_plan_name || claim.insurance_payer_name || '',
    otherHealthPlan: claim.primary_insured_has_other_plan === true ? true : claim.primary_insured_has_other_plan === false ? false : undefined,
    otherClaimId: claim.primary_insured_other_claim_id || '',

    // Box 12
    patientSignature: {
      signed: claim.patient_consent_on_file === true,
      date: localTodayCalendarDate(),
    },

    // Box 13
    insuredSignature: {
      signed: claim.primary_insured_consent_on_file === true,
      date: localTodayCalendarDate(),
    },

    // Box 14
    currentIllnessDate: claim.date_current_illness_injury || '',
    currentIllnessQualifier: '', // Qualifier not in API

    // Box 15
    otherDate: claim.date_similar_illness || '',
    otherDateQualifier: '', // Qualifier not in API

    // Box 16
    unableToWorkFrom: claim.date_unable_to_work_from || '',
    unableToWorkTo: claim.date_unable_to_work_to || '',

    // Box 17
    referringProviderName: claim.referring_provider_name || '',
    referringProviderIdQualifier: '', // Qualifier not in API
    referringProviderNPI: claim.referring_provider_npi || '',

    // Box 18
    hospitalizationFrom: claim.hospitalization_date_from || '',
    hospitalizationTo: claim.hospitalization_date_to || '',

    // Box 19
    additionalClaimInfo: claim.additional_claim_info || '',

    // Box 20
    outsideLab: claim.outside_lab || false,
    outsideLabCharges: claim.outside_lab_charges ? (claim.outside_lab_charges / 100).toFixed(2) : '',

    // Box 21
    icdIndicator: '0', // Default to ICD-10
    diagnosisCodes,

    // Box 22
    resubmissionCode: claim.resubmission_code || '',
    originalRefNo: claim.original_reference_number || '',

    // Box 23
    priorAuthNumber: claim.prior_authorization_number || '',

    // Box 24
    serviceLines,

    // Box 25
    federalTaxIdNumber: claim.tax_id || '',
    taxIdType: (claim.tax_id_type as 'SSN' | 'EIN') || '',

    // Box 26
    patientAccountNo: claim.patient_account_number || '',

    // Box 27
    acceptAssignment: claim.accept_assignment === true,

    // Box 28
    totalCharge: ((claim.total_charge_cents || 0) / 100).toFixed(2),

    // Box 29
    amountPaid: ((claim.amount_paid_cents || 0) / 100).toFixed(2),

    // Box 30
    reservedNUCC30: '',

    // Box 31
    physicianSignature: {
      signed: claim.provider_signature_on_file === true,
      date: localTodayCalendarDate(),
      credentials: '',
    },

    // Box 32
    serviceFacility: {
      name: serviceFacility?.name || '',
      line1: serviceFacility?.line1 || serviceFacility?.address || '',
      line2: serviceFacility?.line2 || serviceFacility?.address2 || '',
      city: serviceFacility?.city || '',
      state: serviceFacility?.state || '',
      zip: serviceFacility?.zip || '',
      npi: claim.service_facility_npi || '',
    },

    // Box 33
    billingProvider: {
      name: claim.provider_display_name || '',
      address: billingAddr?.line1 || '',
      address2: billingAddr?.line2 || '',
      city: billingAddr?.city || '',
      state: billingAddr?.state || '',
      zip: billingAddr?.zip || '',
      phone: billingAddr ? `${billingAddr.area_code || ''}${billingAddr.phone || ''}` : '',
      npi: claim.billing_provider_npi || '',
      otherId: '', // Not in API model
    },
  };
};

/**
 * Transform CMS-1500 form data back to API claim update request format
 */
export const transformFormToClaim = (formData: CMS1500FormData, originalClaim: Claim): ClaimUpdateRequest => {
  // Convert diagnoses back to array format
  const diagnoses: string[] = [];
  const diagnosisLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

  diagnosisLetters.forEach(letter => {
    const code = formData.diagnosisCodes[letter as keyof typeof formData.diagnosisCodes];
    if (code) {
      diagnoses.push(code);
    }
  });

  // Map insurance type from form values to API enum
  const mapInsuranceType = (): InsuranceType | null => {
    if (!formData.insuranceType) return null;
    const typeMap: Record<string, InsuranceType> = {
      'medicare': 'Medicare',
      'medicaid': 'Medicaid',
      'tricare': 'TRICARE',
      'champva': 'CHAMPVA',
      'groupHealth': 'GroupHealthPlan',
      'feca': 'FECA',
      'other': 'Other',
    };
    return typeMap[formData.insuranceType] || null;
  };

  // Convert service lines back to claim_lines format
  const claim_lines = formData.serviceLines
    // Filter out completely empty lines (where all key fields are undefined)
    // Keep lines that have any data, even if CPT code is empty
    .filter(line => 
      line.dateFrom !== undefined || 
      line.cptHcpcs !== undefined || 
      line.charges !== undefined ||
      line.placeOfService !== undefined
    )
    .map((line, index) => {
      // Get the original claim line if it exists
      const originalLine = originalClaim.claim_lines?.[index];
      
      // Build the claim line object
      return {
        // Required fields
        encounter_id: originalLine?.encounter_id || '', // Required by ClaimLine3 type
        line_number: index + 1,
        date_from: line.dateFrom,
        date_to: line.dateTo || line.dateFrom,
        pos_code: line.placeOfService,
        cpt_code: line.cptHcpcs,
        modifiers: [line.modifier1, line.modifier2, line.modifier3, line.modifier4].filter((m): m is string => !!m?.trim()),
        diagnosis_pointers: (line.diagnosisPointer || '').split(',').filter(p => p.trim()),
        charge_cents: Math.round(parseFloat(line.charges || '0') * 100),
        units: parseFloat(line.units || '1'),
        rendering_provider_npi: line.renderingProviderId || null,
        qualifiers: line.idQualifier ? { id_qualifier: line.idQualifier } : null,
        
        // Preserve existing optional fields from original line
        ...(originalLine && {
          id: originalLine.id,
          claim_id: originalLine.claim_id,
          emg: originalLine.emg,
          epsdt_family_plan: originalLine.epsdt_family_plan,
          line_control_number: originalLine.line_control_number,
        }),
      };
    });



  // Merge with original claim, updating only the fields we manage
  return {
    ...originalClaim,

    // Insurance type and IDs
    insurance_type: mapInsuranceType(),
    primary_insured_id_number: formData.insuredIdNumber || null,
    primary_insured_group_number: formData.insuredPolicyGroup || null,
    primary_insured_plan_name: formData.insuredPlanName || null,
    insurance_payer_name: formData.insuredPlanName || null, // Keep for backward compatibility

    // Patient information - split name fields (API uses _middle_initial)
    patient_first_name: formData.patientName.firstName || null,
    patient_middle_initial: formData.patientName.middleInitial || null,
    patient_last_name: formData.patientName.lastName || null,
    patient_dob: formData.patientBirthDate || null,
    patient_sex: formData.patientSex === 'M' ? 'Male' : formData.patientSex === 'F' ? 'Female' : null as Sex | null,
    patient_address: formData.patientAddress.line1 || formData.patientAddress.city ? {
      line1: formData.patientAddress.line1 || null,
      line2: formData.patientAddress.line2 || null,
      city: formData.patientAddress.city || null,
      state: formData.patientAddress.state || null,
      zip: formData.patientAddress.zip || null,
      area_code: formData.patientAddress.phone ? formData.patientAddress.phone.slice(0, 3) : null,
      phone: formData.patientAddress.phone ? formData.patientAddress.phone.slice(3, 10) : null,
    } : null,
    patient_account_number: formData.patientAccountNo || null,

    // Primary insured information - split name fields (API uses _middle_initial)
    primary_insured_first_name: formData.insuredName.firstName || null,
    primary_insured_middle_initial: formData.insuredName.middleInitial || null,
    primary_insured_last_name: formData.insuredName.lastName || null,
    primary_insured_dob: formData.insuredDateOfBirth || null,
    primary_insured_sex: formData.insuredSex === 'M' ? 'Male' : formData.insuredSex === 'F' ? 'Female' : null as Sex | null,
    primary_insured_address: formData.insuredAddress.line1 || formData.insuredAddress.city ? {
      line1: formData.insuredAddress.line1 || null,
      line2: formData.insuredAddress.line2 || null,
      city: formData.insuredAddress.city || null,
      state: formData.insuredAddress.state || null,
      zip: formData.insuredAddress.zip || null,
      area_code: formData.insuredAddress.phone ? formData.insuredAddress.phone.slice(0, 3) : null,
      phone: formData.insuredAddress.phone ? formData.insuredAddress.phone.slice(3, 10) : null,
    } : null,
    primary_insured_relationship: formData.patientRelationship || null,
    primary_insured_has_other_plan: formData.otherHealthPlan || null,
    primary_insured_other_claim_id: formData.otherClaimId || null,

    // Condition related fields
    patient_condition_employment_related: formData.conditionRelated.employment,
    patient_condition_auto_accident: formData.conditionRelated.autoAccident,
    patient_condition_auto_accident_state: formData.conditionRelated.autoAccidentState || null,
    patient_condition_other_accident: formData.conditionRelated.otherAccident,
    patient_condition_claim_codes: formData.conditionRelated.claimCodes || null,

    // Consent/signature fields
    patient_consent_on_file: formData.patientSignature.signed,
    primary_insured_consent_on_file: formData.insuredSignature.signed,
    provider_signature_on_file: formData.physicianSignature.signed,

    // Provider fields (Box 33 and Box 25)
    provider_display_name: formData.billingProvider.name || null,
    billing_provider_npi: formData.billingProvider.npi || null,
    tax_id: formData.federalTaxIdNumber || null,
    tax_id_type: formData.taxIdType === 'SSN' ? 'SSN' : formData.taxIdType === 'EIN' ? 'EIN' : null as TaxIdType | null,
    accept_assignment: formData.acceptAssignment,

    // Financial
    total_charge_cents: Math.round(parseFloat(formData.totalCharge || '0') * 100),
    amount_paid_cents: Math.round(parseFloat(formData.amountPaid || '0') * 100),
    balance_due_cents: Math.round(parseFloat(formData.totalCharge || '0') * 100) - Math.round(parseFloat(formData.amountPaid || '0') * 100),

    // Diagnoses and service lines
    diagnoses,
    claim_lines,

    // Service facility (Box 32)
    service_facility: formData.serviceFacility.name || formData.serviceFacility.line1 ? {
      name: formData.serviceFacility.name || null,
      line1: formData.serviceFacility.line1 || null,
      line2: formData.serviceFacility.line2 || null,
      city: formData.serviceFacility.city || null,
      state: formData.serviceFacility.state || null,
      zip: formData.serviceFacility.zip || null,
    } : null,
    service_facility_npi: formData.serviceFacility.npi || null,

    // Billing address - use proper BillingAddress structure
    billing_address: formData.billingProvider.address || formData.billingProvider.city ? {
      line1: formData.billingProvider.address || null,
      line2: formData.billingProvider.address2 || null,
      city: formData.billingProvider.city || null,
      state: formData.billingProvider.state || null,
      zip: formData.billingProvider.zip || null,
      area_code: formData.billingProvider.phone ? formData.billingProvider.phone.slice(0, 3) : null,
      phone: formData.billingProvider.phone ? formData.billingProvider.phone.slice(3, 10) : null,
    } : null,

    // Other insured information (Box 9) - renamed from secondary (API uses _middle_initial)
    other_insured_first_name: formData.otherInsuredName.firstName || null,
    other_insured_middle_initial: formData.otherInsuredName.middleInitial || null,
    other_insured_last_name: formData.otherInsuredName.lastName || null,
    other_insured_policy_number: formData.otherInsuredPolicyGroup || null,
    // Box 9b DOB/Sex don't exist on CMS-1500 (02/12) - these are now reserved NUCC fields
    // and are not included in UpdateClaimRequest
    other_insured_plan_name: formData.otherInsuredPlanName || null,
    // other_insured_employer_name is Box 9c (reserved)
    
    // Date fields (Boxes 14-18)
    date_current_illness_injury: formData.currentIllnessDate || null,
    date_similar_illness: formData.otherDate || null,
    date_unable_to_work_from: formData.unableToWorkFrom || null,
    date_unable_to_work_to: formData.unableToWorkTo || null,
    hospitalization_date_from: formData.hospitalizationFrom || null,
    hospitalization_date_to: formData.hospitalizationTo || null,

    // Referring provider (Box 17)
    referring_provider_name: formData.referringProviderName || null,
    referring_provider_npi: formData.referringProviderNPI || null,
    
    // Additional claim info (Box 19)
    additional_claim_info: formData.additionalClaimInfo || null,
    
    // Outside lab (Box 20)
    outside_lab: formData.outsideLab || null,
    outside_lab_charges: formData.outsideLabCharges ? Math.round(parseFloat(formData.outsideLabCharges) * 100) : null,
    
    // Resubmission info (Box 22)
    resubmission_code: formData.resubmissionCode || null,
    original_reference_number: formData.originalRefNo || null,
    
    // Prior authorization (Box 23)
    prior_authorization_number: formData.priorAuthNumber || null,

  };
};
